import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, API, apiError, getToken } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { GoldButton } from "@/components/Brand";

export default function AdminSubmissions({ onChange }) {
  const { t, localized } = useI18n();
  const [subs, setSubs] = useState([]);
  const [status, setStatus] = useState("pending");
  const [albumsByEvent, setAlbumsByEvent] = useState({});
  const [choices, setChoices] = useState({});

  const load = useCallback(() => {
    api.get("/admin/submissions", { params: { status } }).then(async (r) => {
      setSubs(r.data);
      const eventIds = [...new Set(r.data.map((s) => s.event_id))];
      const map = {};
      await Promise.all(
        eventIds.map(async (id) => {
          const res = await api.get(`/admin/events/${id}/albums`).catch(() => null);
          if (res) map[id] = res.data;
        })
      );
      setAlbumsByEvent(map);
    });
  }, [status]);

  useEffect(load, [load]);

  const approve = async (sub) => {
    const conf = choices[sub.id] || {};
    try {
      await api.post(`/admin/submissions/${sub.id}/approve`, {
        album_id: conf.album_id || sub.album_id || albumsByEvent[sub.event_id]?.[0]?.id,
        caption_es: conf.caption_es ?? sub.caption_es,
        photographer: conf.photographer ?? sub.photographer,
        download_enabled: conf.download_enabled ?? true,
        watermark: conf.watermark ?? false,
      });
      toast.success(t("admin.submissionApproved"));      load();
      onChange?.();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid="admin-submissions-panel">
      <div className="flex gap-2">
        {["pending", "approved", "rejected", "all"].map((s) => (
          <button
            key={s}
            type="button"
            data-testid={`submissions-filter-${s}`}
            onClick={() => setStatus(s)}
            className={`min-h-[44px] border px-4 text-sm ${
              status === s ? "border-[#8F3329] bg-[#8F3329] text-[#FAFAF7]" : "border-[#C69A3B]/50"
            }`}
          >
            {s}
          </button>
        ))}
      </div>

      {subs.length === 0 && <p className="mt-8 text-sm text-[#5A5752]">{t("account.noUploads")}</p>}

      <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {subs.map((sub) => (
          <article key={sub.id} data-testid={`submission-card-${sub.id}`} className="border border-[#C69A3B]/30 bg-white">
            <div className="aspect-[4/3] overflow-hidden bg-[#D8D3CB]">
              {sub.kind === "photo" ? (
                <img src={`${API}/submissions/${sub.id}/file?auth=${getToken()}`} alt={sub.filename} className="h-full w-full object-cover" />
              ) : (
                <video src={`${API}/submissions/${sub.id}/file?auth=${getToken()}`} controls className="h-full w-full" />
              )}
            </div>
            <div className="space-y-3 p-4 text-sm">
              <p className="truncate font-medium">{sub.filename}</p>
              <p className="text-xs text-[#5A5752]">{sub.user_email} · {sub.status}</p>
              <select
                data-testid={`submission-album-${sub.id}`}
                className="min-h-[42px] w-full border border-[#C69A3B]/60 px-2 text-sm"
                value={choices[sub.id]?.album_id || sub.album_id || ""}
                onChange={(e) => setChoices((c) => ({ ...c, [sub.id]: { ...c[sub.id], album_id: e.target.value } }))}
              >
                <option value="">Álbum…</option>
                {(albumsByEvent[sub.event_id] || []).map((a) => (
                  <option key={a.id} value={a.id}>{localized(a, "name")}</option>
                ))}
              </select>
              <input
                data-testid={`submission-caption-${sub.id}`}
                placeholder="Leyenda"
                defaultValue={sub.caption_es || ""}
                onChange={(e) => setChoices((c) => ({ ...c, [sub.id]: { ...c[sub.id], caption_es: e.target.value } }))}
                className="min-h-[42px] w-full border border-[#C69A3B]/60 px-2 text-sm"
              />
              <input
                data-testid={`submission-photographer-${sub.id}`}
                placeholder={t("gallery.photographer")}
                defaultValue={sub.photographer || ""}
                onChange={(e) => setChoices((c) => ({ ...c, [sub.id]: { ...c[sub.id], photographer: e.target.value } }))}
                className="min-h-[42px] w-full border border-[#C69A3B]/60 px-2 text-sm"
              />
              <div className="flex gap-4 text-xs">
                <label className="flex items-center gap-1">
                  <input
                    type="checkbox"
                    data-testid={`submission-download-${sub.id}`}
                    defaultChecked
                    onChange={(e) => setChoices((c) => ({ ...c, [sub.id]: { ...c[sub.id], download_enabled: e.target.checked } }))}
                  />
                  {t("common.downloads")}
                </label>
                <label className="flex items-center gap-1">
                  <input
                    type="checkbox"
                    data-testid={`submission-watermark-${sub.id}`}
                    onChange={(e) => setChoices((c) => ({ ...c, [sub.id]: { ...c[sub.id], watermark: e.target.checked } }))}
                  />
                  {t("common.watermark")}
                </label>
              </div>
              {sub.status === "pending" && (
                <div className="flex gap-2">
                  <GoldButton data-testid={`submission-approve-${sub.id}`} onClick={() => approve(sub)}>
                    Aprobar
                  </GoldButton>
                  <GoldButton
                    variant="outline"
                    data-testid={`submission-reject-${sub.id}`}
                    onClick={async () => {
                      await api.post(`/admin/submissions/${sub.id}/reject`, { reason: "" });
                      load();
                      onChange?.();
                    }}
                  >
                    Rechazar
                  </GoldButton>
                </div>
              )}
            </div>
          </article>
        ))}
      </div>
    </div>
  );
}
